import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IWhatsNewItem, IWhatsNewToursResponse } from "api/whatsNew/whatsNew.type";

// App-wide What's New state (bell lives in the global top bar, tours arm hero buttons across pages,
// the modal is mounted once in Layout). Feed pages beyond the first are fetched locally by WhatsNewPage
// and NOT cached here — `items` holds the boot page-1 slice, which powers the panel and the tour lookup.
interface WhatsNewState {
  /** Boot page-1 feed slice — the panel's list and the source for resolving a tour item by id. */
  items: IWhatsNewItem[];
  /** Per-user unread count from the feed (bell dot when > 0). */
  unreadCount: number;
  /** page_key -> { id, seen } for the feature-page hero tours. */
  tours: IWhatsNewToursResponse;
  /** Item currently shown in the global FeatureTourModal (null = closed). */
  activeTourItem: IWhatsNewItem | null;
  /** Boot fetch (feed + tours) completed. */
  loaded: boolean;
  /**
   * Locale the cached `items` were fetched with. Feed content is localized SERVER-side from the
   * request's `Accept-Language`, so switching language makes the cache stale — `useWhatsNewBoot`
   * compares this against the live `i18n.language` to know it must refetch. Set even when the
   * fetch fails, so a failing boot doesn't refetch on every Layout remount.
   */
  locale: string | null;
}

const initialState: WhatsNewState = {
  items: [],
  unreadCount: 0,
  tours: {},
  activeTourItem: null,
  loaded: false,
  locale: null,
};

export const whatsNewSlice = createSlice({
  name: "whatsNew",
  initialState,
  reducers: {
    setWhatsNewFeed: (state, action: PayloadAction<{ items: IWhatsNewItem[]; unreadCount: number }>) => {
      state.items = action.payload.items;
      state.unreadCount = action.payload.unreadCount;
      // `activeTourItem` is a COPY of a feed item, not a reference into `items`, so an open tour modal
      // would keep rendering the previous language after a locale refetch. Re-resolve it by id from the
      // incoming page; if the item is no longer on it, keep what is on screen rather than blanking it.
      if (state.activeTourItem) {
        const refreshed = action.payload.items.find((i) => i.id === state.activeTourItem?.id);
        if (refreshed) state.activeTourItem = refreshed;
      }
    },
    setWhatsNewUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload;
    },
    setWhatsNewTours: (state, action: PayloadAction<IWhatsNewToursResponse>) => {
      state.tours = action.payload;
    },
    // Optimistically flip a tour's seen flag after the auto-open write, so it doesn't re-open this session.
    markWhatsNewTourSeen: (state, action: PayloadAction<string>) => {
      const tour = state.tours[action.payload];
      if (tour) tour.seen = true;
    },
    setActiveTourItem: (state, action: PayloadAction<IWhatsNewItem | null>) => {
      state.activeTourItem = action.payload;
    },
    setWhatsNewLoaded: (state, action: PayloadAction<boolean>) => {
      state.loaded = action.payload;
    },
    /** Records which locale the cached feed was fetched with. See `WhatsNewState.locale`. */
    setWhatsNewLocale: (state, action: PayloadAction<string>) => {
      state.locale = action.payload;
    },
  },
});

export const { setWhatsNewFeed, setWhatsNewUnreadCount, setWhatsNewTours, markWhatsNewTourSeen, setActiveTourItem, setWhatsNewLoaded, setWhatsNewLocale } = whatsNewSlice.actions;

export default whatsNewSlice.reducer;
